import { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { categorizeTechnicalFile } from '../lib/technicalFile';
import type {
  EvidenceDocument,
  EvidenceVersionComparison,
  RegulationPack,
} from '../types';

interface TechnicalFileStructureProps {
  documents: EvidenceDocument[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  projectId?: string;
  onVersionUploaded?: () => void;
  regulationPacks?: RegulationPack[];
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

export default function TechnicalFileStructure({
  documents,
  isLoading,
  error,
  onRetry,
  projectId,
  onVersionUploaded,
  regulationPacks = [],
}: TechnicalFileStructureProps) {
  const { t } = useTranslation();
  const [uploadingDocumentId, setUploadingDocumentId] = useState<string | null>(
    null
  );
  const [versionUploadError, setVersionUploadError] = useState<string | null>(
    null
  );
  const [comparison, setComparison] = useState<{
    documentId: string;
    data: EvidenceVersionComparison | null;
    isLoading: boolean;
    error: string | null;
  } | null>(null);
  const categories = useMemo(
    () => categorizeTechnicalFile(documents, regulationPacks),
    [documents, regulationPacks]
  );
  const coveredCategories = categories.filter(
    category => category.documents.length > 0
  ).length;
  const coveragePercent = Math.round(
    (coveredCategories / categories.length) * 100
  );

  const handleVersionUpload = async (
    document: EvidenceDocument,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !projectId) {
      return;
    }

    setVersionUploadError(null);
    setUploadingDocumentId(document.id);
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      await api.post(
        `/api/projects/${projectId}/evidence/${document.id}/versions`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      onVersionUploaded?.();
    } catch {
      setVersionUploadError(t('results.technicalFile.versionUploadError'));
    } finally {
      setUploadingDocumentId(null);
    }
  };

  const handleCompareVersions = async (document: EvidenceDocument) => {
    if (!projectId) {
      return;
    }

    if (comparison?.documentId === document.id && comparison.data) {
      setComparison(null);
      return;
    }

    setComparison({
      documentId: document.id,
      data: null,
      isLoading: true,
      error: null,
    });
    try {
      const response = await api.get<EvidenceVersionComparison>(
        `/api/projects/${projectId}/evidence/${document.id}/versions/compare`
      );
      setComparison({
        documentId: document.id,
        data: response.data,
        isLoading: false,
        error: null,
      });
    } catch {
      setComparison({
        documentId: document.id,
        data: null,
        isLoading: false,
        error: t('results.technicalFile.versionCompareError'),
      });
    }
  };

  return (
    <section
      id="technical-file"
      className="results-panel"
      style={{ marginBottom: 24, padding: '24px 28px', scrollMarginTop: 24 }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 18,
          marginBottom: 18,
        }}
      >
        <div style={{ maxWidth: 680 }}>
          <h2
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontSize: 18,
              fontWeight: 700,
              color: 'var(--text-primary)',
            }}
          >
            {t('results.technicalFile.title')}
          </h2>
          <p
            style={{
              margin: '6px 0 0',
              color: 'var(--text-secondary)',
              fontSize: 14,
              lineHeight: 1.55,
            }}
          >
            {t('results.technicalFile.subtitle')}
          </p>
        </div>

        {!isLoading && !error && (
          <div style={{ minWidth: 220 }}>
            <div
              style={{
                marginBottom: 7,
                color: 'var(--text-secondary)',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {t('results.technicalFile.coverage', {
                covered: coveredCategories,
                total: categories.length,
              })}
            </div>
            <div
              role="progressbar"
              aria-label={t('results.technicalFile.coverage', {
                covered: coveredCategories,
                total: categories.length,
              })}
              aria-valuemin={0}
              aria-valuemax={categories.length}
              aria-valuenow={coveredCategories}
              style={{
                height: 8,
                overflow: 'hidden',
                borderRadius: 999,
                background: 'var(--bg-tertiary)',
              }}
            >
              <div
                style={{
                  width: `${coveragePercent}%`,
                  height: '100%',
                  borderRadius: 999,
                  background: 'var(--accent-primary)',
                  transition: 'width 0.2s ease',
                }}
              />
            </div>
          </div>
        )}
      </div>

      {isLoading ? (
        <div style={{ color: 'var(--text-muted)', padding: '20px 0' }}>
          {t('results.technicalFile.loading')}
        </div>
      ) : error ? (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '12px 14px',
            background: 'var(--danger-dim)',
            border: '1px solid rgba(255,0,0,0.2)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--danger)',
          }}
        >
          <span>{error}</span>
          <button
            type="button"
            className="button button-secondary"
            onClick={onRetry}
            style={{ minHeight: 'auto', padding: '7px 12px' }}
          >
            {t('results.technicalFile.retry')}
          </button>
        </div>
      ) : (
        <>
          {versionUploadError && (
            <div
              role="alert"
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--danger-dim)',
                color: 'var(--danger)',
                fontSize: 12,
              }}
            >
              {versionUploadError}
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
              gap: 12,
            }}
          >
            {categories.map(category => {
              const present = category.documents.length > 0;
              return (
                <article
                  key={category.id}
                  style={{
                    minWidth: 0,
                    padding: '16px',
                    border: `1px solid ${
                      present ? 'rgba(0,230,118,0.24)' : 'var(--border)'
                    }`,
                    borderRadius: 'var(--radius-md)',
                    background: present
                      ? 'var(--success-dim)'
                      : 'var(--bg-secondary)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 10,
                      marginBottom: 10,
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        color: 'var(--text-primary)',
                        fontSize: 14,
                        lineHeight: 1.4,
                      }}
                    >
                      {t(`results.technicalFile.categories.${category.id}`)}
                    </h3>
                    <span
                      style={{
                        flexShrink: 0,
                        color: present ? 'var(--success)' : 'var(--warning)',
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {present
                        ? t('results.technicalFile.present')
                        : t('results.technicalFile.missing')}
                    </span>
                  </div>

                  {present ? (
                    <div style={{ display: 'grid', gap: 8 }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        {t('results.technicalFile.documents', {
                          count: category.documents.length,
                        })}
                      </div>
                      {category.documents.map(document => (
                        <div
                          key={document.id}
                          style={{
                            minWidth: 0,
                            paddingTop: 8,
                            borderTop: '1px solid var(--border)',
                          }}
                        >
                          <div
                            title={document.filename}
                            style={{
                              overflow: 'hidden',
                              color: 'var(--text-primary)',
                              fontSize: 13,
                              fontWeight: 700,
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {document.filename}
                          </div>
                          <div
                            style={{
                              marginTop: 3,
                              color: 'var(--text-muted)',
                              fontSize: 11,
                              lineHeight: 1.4,
                            }}
                          >
                            {t(
                              `upload.documentType.options.${document.documentType}`,
                              { defaultValue: document.documentType }
                            )}
                            {' · '}
                            {formatBytes(document.fileSize)}
                            {' · '}
                            {t('results.technicalFile.versionLabel', {
                              version: document.currentVersionNumber || 1,
                            })}
                            {' · '}
                            {t(
                              `results.technicalFile.extractionStatus.${document.extractionStatus}`,
                              { defaultValue: document.extractionStatus }
                            )}
                          </div>
                          <div
                            style={{
                              marginTop: 8,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                color: 'var(--text-muted)',
                                fontSize: 11,
                              }}
                            >
                              {t('results.technicalFile.versionCount', {
                                count:
                                  document.versionCount ||
                                  document.versions?.length ||
                                  1,
                              })}
                            </span>
                            {projectId && (
                              <label
                                className="button button-secondary"
                                style={{
                                  minHeight: 'auto',
                                  padding: '5px 8px',
                                  cursor:
                                    uploadingDocumentId === document.id
                                      ? 'not-allowed'
                                      : 'pointer',
                                  fontSize: 11,
                                }}
                              >
                                {uploadingDocumentId === document.id
                                  ? t('results.technicalFile.uploadingVersion')
                                  : t('results.technicalFile.uploadVersion')}
                                <input
                                  type="file"
                                  accept="application/pdf"
                                  disabled={uploadingDocumentId !== null}
                                  onChange={event =>
                                    handleVersionUpload(document, event)
                                  }
                                  style={{ display: 'none' }}
                                />
                              </label>
                            )}
                          </div>
                          {document.versions &&
                            document.versions.length > 1 && (
                              <>
                                <ol
                                  style={{
                                    margin: '8px 0 0',
                                    paddingLeft: 16,
                                    color: 'var(--text-muted)',
                                    fontSize: 11,
                                    lineHeight: 1.45,
                                  }}
                                >
                                  {document.versions.map(version => (
                                    <li key={version.id}>
                                      {t('results.technicalFile.versionLabel', {
                                        version: version.versionNumber,
                                      })}
                                      {' · '}
                                      {version.filename}
                                    </li>
                                  ))}
                                </ol>
                                {projectId && (
                                  <button
                                    type="button"
                                    className="button button-secondary"
                                    onClick={() =>
                                      handleCompareVersions(document)
                                    }
                                    style={{
                                      marginTop: 8,
                                      minHeight: 'auto',
                                      padding: '5px 8px',
                                      fontSize: 11,
                                    }}
                                  >
                                    {comparison?.documentId === document.id &&
                                    comparison.isLoading
                                      ? t(
                                          'results.technicalFile.comparingVersions'
                                        )
                                      : t(
                                          'results.technicalFile.compareVersions'
                                        )}
                                  </button>
                                )}
                              </>
                            )}
                          {comparison?.documentId === document.id && (
                            <VersionComparisonPanel comparison={comparison} />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                      {t('results.technicalFile.missing')}
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          <p
            style={{
              margin: '16px 0 0',
              color: 'var(--text-muted)',
              fontSize: 12,
              lineHeight: 1.55,
            }}
          >
            {t('results.technicalFile.presenceNote')}
          </p>
        </>
      )}
    </section>
  );
}

function VersionComparisonPanel({
  comparison,
}: {
  comparison: {
    data: EvidenceVersionComparison | null;
    isLoading: boolean;
    error: string | null;
  };
}) {
  const { t } = useTranslation();
  if (comparison.isLoading) {
    return (
      <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: 11 }}>
        {t('results.technicalFile.comparingVersions')}
      </div>
    );
  }
  if (comparison.error) {
    return (
      <div
        role="alert"
        style={{ marginTop: 8, color: 'var(--danger)', fontSize: 11 }}
      >
        {comparison.error}
      </div>
    );
  }
  if (!comparison.data) {
    return null;
  }

  const changedSegments = comparison.data.diff.filter(
    segment => segment.type !== 'unchanged'
  );

  return (
    <div
      style={{
        marginTop: 10,
        padding: 10,
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--bg-primary)',
      }}
    >
      <div
        style={{
          marginBottom: 8,
          color: 'var(--text-secondary)',
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {t('results.technicalFile.comparisonTitle', {
          from: comparison.data.fromVersion.versionNumber,
          to: comparison.data.toVersion.versionNumber,
        })}
      </div>

      {comparison.data.reviewChecklistItemCount > 0 && (
        <div
          style={{
            marginBottom: 8,
            color: 'var(--warning)',
            fontSize: 11,
            lineHeight: 1.45,
          }}
        >
          {t('results.technicalFile.reviewItemsMarked', {
            count: comparison.data.reviewChecklistItemCount,
          })}
        </div>
      )}

      {!comparison.data.hasChanges ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
          {t('results.technicalFile.noVersionChanges')}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 4 }}>
          {changedSegments.slice(0, 40).map((segment, index) => (
            <div
              key={`${segment.type}-${index}-${segment.text}`}
              style={{
                padding: '4px 6px',
                borderRadius: 6,
                background:
                  segment.type === 'added'
                    ? 'var(--success-dim)'
                    : 'var(--danger-dim)',
                color:
                  segment.type === 'added' ? 'var(--success)' : 'var(--danger)',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
              }}
            >
              {segment.type === 'added' ? '+ ' : '- '}
              {segment.text}
            </div>
          ))}
          {changedSegments.length > 40 && (
            <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              {t('results.technicalFile.moreVersionChanges', {
                count: changedSegments.length - 40,
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
